/*
 * <gl-insta-stories>: story circles plus a modal viewer.
 * Images advance after data-autoplay-seconds, videos when they end.
 * Story media is only created while the viewer shows it.
 */
(() => {
  if (customElements.get('gl-insta-stories')) return;

  const MS_PER_SECOND = 1000;
  const DEFAULT_IMAGE_SECONDS = 10;
  const SWIPE_MIN_PX = 50;
  const SCROLL_STEP_RATIO = 0.8;
  // Scroll snapping can leave the row a few pixels off its ends.
  const SCROLL_EDGE_PX = 4;

  const createElement = (tag, attributes = {}) => {
    const element = document.createElement(tag);
    Object.entries(attributes).forEach(([name, value]) => {
      if (value !== null && value !== undefined) element.setAttribute(name, value);
    });
    return element;
  };

  class GlInstaStories extends HTMLElement {
    connectedCallback() {
      this.viewer = this.querySelector('[data-viewer]');
      this.list = this.querySelector('[data-list]');
      if (!this.viewer || !this.list || this.listeners) return;

      this.users = this.readUsers();
      this.imageSeconds = Number(this.dataset.autoplaySeconds) || DEFAULT_IMAGE_SECONDS;
      this.muted = false;
      this.paused = false;
      this.autoPaused = false;
      this.frameRequest = null;
      this.current = null;
      // One controller removes every listener when the section is re-rendered in the editor.
      this.listeners = new AbortController();

      this.ui = {
        frame: this.viewer.querySelector('[data-frame]'),
        media: this.viewer.querySelector('[data-media]'),
        progress: this.viewer.querySelector('[data-progress]'),
        avatar: this.viewer.querySelector('[data-avatar]'),
        name: this.viewer.querySelector('[data-name]'),
        verified: this.viewer.querySelector('[data-verified]'),
        posted: this.viewer.querySelector('[data-posted]'),
        pause: this.viewer.querySelector('[data-pause]'),
        mute: this.viewer.querySelector('[data-mute]'),
        peekPrev: this.viewer.querySelector('[data-peek="-1"]'),
        peekNext: this.viewer.querySelector('[data-peek="1"]'),
      };

      this.bindList();
      this.bindViewer();
    }

    disconnectedCallback() {
      this.stopPlayback();
      this.listeners?.abort();
      this.listeners = null;
      this.resizeObserver?.disconnect();
      if (this.viewer?.open) document.body.classList.remove('overflow-hidden');
    }

    readUsers() {
      try {
        const data = JSON.parse(this.querySelector('[data-stories]')?.textContent || '[]');
        // Order must match the circles' data-open indexes, so users without stories stay in and are skipped later.
        return Array.isArray(data) ? data : [];
      } catch (error) {
        console.error('gl-insta-stories: invalid story data', error);
        return [];
      }
    }

    /* ---------- Circles ---------- */

    bindList() {
      const { signal } = this.listeners;
      this.list.addEventListener(
        'click',
        (event) => {
          const button = event.target.closest('[data-open]');
          if (button) this.open(Number(button.dataset.open), button);
        },
        { signal }
      );
      this.querySelectorAll('[data-scroll]').forEach((button) =>
        button.addEventListener(
          'click',
          () => this.list.scrollBy({ left: Number(button.dataset.scroll) * this.list.clientWidth * SCROLL_STEP_RATIO }),
          { signal }
        )
      );
      this.list.addEventListener('scroll', () => this.updateScrollButtons(), { passive: true, signal });
      this.resizeObserver = new ResizeObserver(() => this.updateScrollButtons());
      this.resizeObserver.observe(this.list);
    }

    updateScrollButtons() {
      const maxScroll = this.list.scrollWidth - this.list.clientWidth;
      this.querySelector('[data-scroll="-1"]').hidden = this.list.scrollLeft <= SCROLL_EDGE_PX;
      this.querySelector('[data-scroll="1"]').hidden = this.list.scrollLeft >= maxScroll - SCROLL_EDGE_PX;
    }

    /* ---------- Viewer ---------- */

    bindViewer() {
      const { signal } = this.listeners;
      this.viewer.addEventListener('click', (event) => this.onViewerClick(event), { signal });
      this.viewer.addEventListener('close', () => this.onClosed(), { signal });
      this.viewer.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'ArrowRight') this.step(1);
          if (event.key === 'ArrowLeft') this.step(-1);
        },
        { signal }
      );
      this.bindSwipe(signal);
      // Pause while the page is hidden and resume afterwards, unless the visitor paused it.
      document.addEventListener(
        'visibilitychange',
        () => {
          if (!this.viewer.open) return;
          if (document.hidden && !this.paused) {
            this.autoPaused = true;
            this.setPaused(true);
          } else if (!document.hidden && this.autoPaused) {
            this.autoPaused = false;
            this.setPaused(false);
          }
        },
        { signal }
      );
    }

    onViewerClick(event) {
      if (event.target === this.viewer || event.target.classList.contains('gl-stories__stage')) return this.close();
      const step = event.target.closest('[data-step]');
      if (step) return this.step(Number(step.dataset.step));
      const peek = event.target.closest('[data-peek]');
      if (peek) return this.goToUser(this.userIndex + Number(peek.dataset.peek));
      if (event.target.closest('[data-close]')) return this.close();
      if (event.target.closest('[data-pause]')) {
        this.autoPaused = false;
        return this.setPaused(!this.paused);
      }
      if (event.target.closest('[data-mute]')) this.setMuted(!this.muted);
    }

    bindSwipe(signal) {
      let startX = null;
      this.ui.frame.addEventListener('touchstart', (event) => { startX = event.touches[0].clientX; }, { passive: true, signal });
      this.ui.frame.addEventListener(
        'touchend',
        (event) => {
          if (startX === null) return;
          const deltaX = event.changedTouches[0].clientX - startX;
          startX = null;
          if (Math.abs(deltaX) >= SWIPE_MIN_PX) this.goToUser(this.userIndex + (deltaX < 0 ? 1 : -1));
        },
        { passive: true, signal }
      );
    }

    hasStories(index) {
      return Array.isArray(this.users[index]?.stories) && this.users[index].stories.length > 0;
    }

    /** Nearest user with stories from `index` in `direction`, or -1. */
    findUser(index, direction) {
      for (let i = index; i >= 0 && i < this.users.length; i += direction) {
        if (this.hasStories(i)) return i;
      }
      return -1;
    }

    open(userIndex, opener) {
      if (!this.hasStories(userIndex)) return;
      this.opener = opener;
      this.viewer.showModal();
      document.body.classList.add('overflow-hidden');
      this.showUser(userIndex, 0);
    }

    close() {
      if (this.viewer.open) this.viewer.close();
    }

    onClosed() {
      this.stopPlayback();
      this.current = null;
      this.ui.media.replaceChildren();
      document.body.classList.remove('overflow-hidden');
      this.opener?.focus();
    }

    step(direction) {
      const next = this.storyIndex + direction;
      if (next >= 0 && next < this.users[this.userIndex].stories.length) return this.showStory(next);
      this.goToUser(this.userIndex + direction);
    }

    /** Moving before the first user restarts it; moving past the last one closes the viewer. */
    goToUser(index) {
      const direction = index < this.userIndex ? -1 : 1;
      const target = this.findUser(index, direction);
      if (target !== -1) return this.showUser(target, 0);
      if (direction < 0) return this.showStory(0);
      this.close();
    }

    showUser(index, storyIndex) {
      if (!this.hasStories(index)) return this.close();
      const user = this.users[index];
      this.userIndex = index;
      this.list.querySelector(`[data-open="${index}"]`)?.classList.add('gl-stories__user--seen');
      this.renderUser(user);
      this.renderPeeks();
      this.showStory(storyIndex);
    }

    renderUser(user) {
      this.ui.name.textContent = user.username || '';
      this.ui.verified.hidden = !user.verified;
      this.ui.avatar.hidden = !user.avatar;
      if (user.avatar) this.ui.avatar.src = user.avatar;
      this.ui.progress.replaceChildren(
        ...user.stories.map(() => {
          const segment = createElement('div', { class: 'gl-stories__segment' });
          segment.append(createElement('span'));
          return segment;
        })
      );
    }

    renderPeeks() {
      [
        [this.ui.peekPrev, this.users[this.findUser(this.userIndex - 1, -1)]],
        [this.ui.peekNext, this.users[this.findUser(this.userIndex + 1, 1)]],
      ].forEach(([peek, user]) => {
        if (!peek) return;
        peek.hidden = !user;
        peek.replaceChildren();
        peek.style.backgroundImage = user?.stories[0]?.thumb ? `url("${user.stories[0].thumb}")` : '';
        if (!user) return;
        if (user.avatar) peek.append(createElement('img', { src: user.avatar, alt: '' }));
        const name = createElement('span');
        name.textContent = user.username || '';
        peek.append(name);
      });
    }

    showStory(index) {
      this.stopPlayback();
      this.storyIndex = index;
      const story = this.users[this.userIndex].stories[index];
      this.ui.posted.textContent = story.posted || '';
      this.ui.frame.style.background = story.background || '';
      this.ui.frame.style.setProperty('--gl-story-fit', story.fit === 'cover' ? 'cover' : 'contain');
      this.ui.mute.hidden = story.type !== 'video';
      this.segments().forEach((segment, i) => this.setSegment(segment, i < index ? 1 : 0));

      const media = story.type === 'video' ? this.createVideo(story) : createElement('img', { src: story.src, alt: '' });
      this.ui.media.replaceChildren(media);
      this.current = { story, media, startedAt: null, elapsedMs: 0 };
      this.keepFocusInside();
      this.setPaused(false);
    }

    /** A control that just got hidden (mute on image stories, a peek at the ends) would drop focus to <body>. */
    keepFocusInside() {
      if (!this.viewer.contains(document.activeElement) || document.activeElement.hidden) {
        this.viewer.focus({ preventScroll: true });
      }
    }

    createVideo(story) {
      const video = createElement('video', { src: story.src, poster: story.poster, playsinline: '', preload: 'auto' });
      const isCurrent = () => this.current?.media === video;
      video.muted = this.muted;
      video.addEventListener('ended', () => isCurrent() && this.step(1));
      // A broken or missing file moves on instead of leaving a blank story.
      video.addEventListener('error', () => isCurrent() && this.step(1));
      // timeupdate keeps the bar moving even when animation frames are throttled.
      video.addEventListener('timeupdate', () => isCurrent() && this.setSegment(this.segments()[this.storyIndex], this.progressRatio()));
      return video;
    }

    segments() {
      return Array.from(this.ui.progress.children);
    }

    setSegment(segment, ratio) {
      segment?.firstElementChild?.style.setProperty('--gl-progress', String(Math.min(1, Math.max(0, ratio))));
    }

    setPaused(paused) {
      this.paused = paused;
      this.ui.pause.setAttribute('aria-pressed', String(paused));
      if (!this.current) return;
      if (paused) return this.stopPlayback();
      this.play();
    }

    setMuted(muted) {
      this.muted = muted;
      this.ui.mute.setAttribute('aria-pressed', String(muted));
      if (this.current?.media instanceof HTMLVideoElement) this.current.media.muted = muted;
    }

    play() {
      const { media } = this.current;
      if (media instanceof HTMLVideoElement) this.playVideo(media);
      this.current = { ...this.current, startedAt: performance.now() };
      this.tick();
    }

    playVideo(video) {
      video.play().catch((error) => {
        // pause() or a story change interrupted this play() call: nothing to recover.
        if (this.current?.media !== video || error.name === 'AbortError') return;
        if (error.name !== 'NotAllowedError') {
          console.error('gl-insta-stories: video failed to play', error);
          return this.step(1);
        }
        // The browser refused sound: run the story muted instead.
        this.setMuted(true);
        video.play().catch((retryError) => console.error('gl-insta-stories: muted video failed to play', retryError));
      });
    }

    tick() {
      this.frameRequest = requestAnimationFrame(() => {
        if (!this.current || this.paused) return;
        const ratio = this.progressRatio();
        this.setSegment(this.segments()[this.storyIndex], ratio);
        if (ratio >= 1 && !(this.current.media instanceof HTMLVideoElement)) return this.step(1);
        this.tick();
      });
    }

    progressRatio() {
      const { media, startedAt, elapsedMs } = this.current;
      if (media instanceof HTMLVideoElement) {
        return media.duration ? media.currentTime / media.duration : 0;
      }
      const runningMs = startedAt === null ? 0 : performance.now() - startedAt;
      return (elapsedMs + runningMs) / (this.imageSeconds * MS_PER_SECOND);
    }

    stopPlayback() {
      cancelAnimationFrame(this.frameRequest);
      if (!this.current) return;
      const { media, startedAt } = this.current;
      if (media instanceof HTMLVideoElement) media.pause();
      if (startedAt !== null) {
        this.current = { ...this.current, elapsedMs: this.current.elapsedMs + performance.now() - startedAt, startedAt: null };
      }
    }
  }

  customElements.define('gl-insta-stories', GlInstaStories);
})();
